/**
 * @CODE:ACC-003-ROOMS-001
 * RoomsTab Component
 *
 * Rooms section for accommodation detail page:
 * - Displays accommodation rooms with details
 * - Room cards with name, capacity, price, amenities
 * - Select button (disabled with tooltip for future booking)
 * - Empty state handling
 * - Responsive grid layout
 * - Fetches real data from Supabase using useRooms hook
 * - Image gallery with main image + thumbnails
 */

import { useState } from "react";
import { Users, Wind, Wifi, Coffee, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { AccommodationWithDetails } from "../types";
import { useRooms } from "../hooks";
import { formatRoomPrice } from "../api";
import { cleanDescriptionText } from "../utils/textFormatters";

interface RoomsTabProps {
  /** Accommodation data */
  accommodation: AccommodationWithDetails;
  /** Optional CSS class */
  className?: string;
}

/**
 * Get amenity icon for display
 */
function getAmenityIcon(amenityName: string): React.ReactNode {
  const iconName = amenityName.toLowerCase();

  if (iconName.includes("wifi") || iconName.includes("와이파이")) {
    return <Wifi className="h-4 w-4" />;
  }
  if (iconName.includes("에어컨") || iconName.includes("냉방")) {
    return <Wind className="h-4 w-4" />;
  }
  if (iconName.includes("미니바") || iconName.includes("냉장고")) {
    return <Coffee className="h-4 w-4" />;
  }
  return null;
}

/**
 * RoomCard component with image gallery
 */
interface RoomCardProps {
  room: any;
}

const RoomCard = ({ room }: RoomCardProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const images = room.images || [];
  const hasImages = images.length > 0;

  return (
    <article
      key={room.id}
      data-testid={`room-card-${room.id}`}
      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
    >
      {/* Room Images */}
      {hasImages && (
        <div className="bg-gray-100">
          {/* Main Image */}
          <div className="relative h-48 cursor-pointer" onClick={() => setIsModalOpen(true)}>
            <img
              src={images[selectedImageIndex].image_url}
              alt={room.name}
              className="w-full h-full object-cover transition-transform hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black opacity-0 hover:opacity-10 transition-opacity" />
          </div>

          {/* Image Modal */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="p-0 border-0 bg-transparent max-w-fit [&>button]:hidden">
              {/* Image with Close Button Overlay */}
              <div className="relative">
                <img
                  src={images[selectedImageIndex].image_url}
                  alt={room.name}
                  className="h-auto w-auto object-contain rounded-lg"
                  style={{
                    maxHeight: 'calc(90vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
                    maxWidth: 'calc(90vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))'
                  }}
                />
                {/* Close Button Overlay */}
                <DialogClose className="absolute -right-3 -top-3 z-50 rounded-full bg-white shadow-lg hover:bg-gray-100 p-2 transition-colors">
                  <X className="h-5 w-5 text-gray-800" />
                  <span className="sr-only">닫기</span>
                </DialogClose>
              </div>
            </DialogContent>
          </Dialog>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-2 p-2 overflow-x-auto">
              {images.map((image: any, index: number) => (
                <button
                  key={image.id}
                  onClick={() => setSelectedImageIndex(index)}
                  className={cn(
                    "flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all",
                    selectedImageIndex === index
                      ? "border-blue-600 opacity-100"
                      : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img
                    src={image.image_url}
                    alt={`${room.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Room Header with Price */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {room.name}
          </h3>
          {room.women_only && (
            <Badge className="bg-pink-100 text-pink-700 border-pink-200">
              여성 전용
            </Badge>
          )}
        </div>
        {room.type && (
          <p className="text-xs text-gray-500 mb-2">{room.type}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <Users className="h-4 w-4" />
            <span className="text-sm">
              기본 {room.capacity_standard || "-"}명
              {room.capacity_max && room.capacity_max !== room.capacity_standard && (
                <span className="text-gray-500"> / 최대 {room.capacity_max}명</span>
              )}
            </span>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">평일 / 주말</p>
            <p className="text-sm font-bold text-blue-600">
              {formatRoomPrice(room.price_weekday)} / {formatRoomPrice(room.price_weekend)}
            </p>
          </div>
        </div>
      </div>

      {/* Room Body */}
      <div className="p-4 space-y-4">
        {/* Description */}
        {room.description && (
          <p className="text-sm text-gray-600 whitespace-pre-line">
            {cleanDescriptionText(room.description)}
          </p>
        )}

        {/* Amenities */}
        {room.amenities && room.amenities.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              편의시설
            </p>
            <div className="flex flex-wrap gap-2">
              {room.amenities.map((amenity: any, idx: number) => (
                <Badge
                  key={`${room.id}-amenity-${idx}`}
                  variant="outline"
                  className="flex items-center gap-1.5 text-xs"
                >
                  {/* iOS Safari fix: Simplified rendering without Fragment */}
                  {getAmenityIcon(amenity.name)}
                  <span>{amenity.name}</span>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="p-4 border-t bg-gray-50">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled
                className="w-full"
                aria-label={`${room.name} 선택 버튼 (미지원)`}
              >
                선택 (준비 중)
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>예약 기능은 준비 중입니다</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </article>
  );
};

/**
 * RoomsTab component showing accommodation rooms from Supabase
 * Displays room cards with details and disabled select buttons
 */
export const RoomsTab = ({
  accommodation,
  className,
}: RoomsTabProps) => {
  // Fetch rooms data using useRooms hook
  const {
    data: rooms,
    isLoading,
    isError,
  } = useRooms(String(accommodation.accommodation_id));

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn("w-full flex items-center justify-center py-12", className)}
        data-testid="rooms-loading"
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">객실 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div
        className={cn("w-full", className)}
        data-testid="rooms-error"
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-900 font-semibold mb-2">
            객실 정보를 불러올 수 없습니다
          </p>
          <p className="text-red-700 text-sm">
            잠시 후 다시 시도해주세요.
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!rooms || rooms.length === 0) {
    return (
      <div
        className={cn("w-full", className)}
        data-testid="rooms-empty"
      >
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">
            현재 등록된 객실 정보가 없습니다.
          </p>
          {accommodation.website && (
            <p className="text-sm text-gray-500 mt-2">
              자세한 정보는{" "}
              <a
                href={accommodation.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                공식 웹사이트
              </a>
              에서 확인하세요.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-6", className)}>
      <div data-testid="rooms-container" className="space-y-4">
        {/* Room Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room) => (
            <RoomCard key={room.id} room={room} />
          ))}
        </div>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          더 자세한 객실 정보와 예약은{" "}
          {accommodation.website && (
            <a
              href={accommodation.website}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700 underline"
            >
              공식 웹사이트
            </a>
          )}
          {!accommodation.website && "호텔의 공식 웹사이트"}에서 확인하세요.
        </p>
      </div>
    </div>
  );
};
